import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Info as AboutIcon,
  Gavel as PracticeAreasIcon,
  LibraryBooks as ResourcesIcon,
  MenuBook as GlossaryIcon, 
  QuestionAnswer as FAQIcon,
  LiveHelp as PracticeAreaFAQIcon,

  ContactMail as ContactIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components for custom UI elements
const NavBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#0e3158", // Darker blue from the image
  boxShadow: "none",
  position: "fixed",
}));

const NavButton = styled(Button)(({ theme, $active }) => ({
  color: $active ? "#f0b94d" : "white", 
  margin: theme.spacing(0, 0.5),
  padding: theme.spacing(2, 1.5),
  fontWeight: 500,
  fontSize: "0.95rem",
  borderRadius: 0,
  textTransform: "none",
  position: "relative",
  "&:hover": {
    backgroundColor: "transparent",
    color: "#f0b94d",
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  letterSpacing: "0.5px",
  fontSize: "1.3rem",
  color: "white",
  textTransform: "uppercase",
  display: "inline-block",
}));

const PhoneNumber = styled(Typography)(({ theme }) => ({
  color: "white",
  display: "flex",
  alignItems: "center",
  fontSize: "0.9rem",
  "& svg": {
    marginRight: theme.spacing(0.5),
  }
}));

const menuItems = [
  { name: "Home", path: "/", icon: <HomeIcon fontSize="small" /> },
  { name: "About Us", path: "/about-us", icon: <AboutIcon fontSize="small" /> },
  { name: "Practice Areas", path: "/practice-areas", icon: <PracticeAreasIcon fontSize="small" /> },
  { name: "Legal Resources", path: "/legal-resources", icon: <ResourcesIcon fontSize="small" /> },

  { name: "Contact", path: "/contact-us", icon: <ContactIcon fontSize="small" /> },
];

// For mobile drawer
const getFlatMenuItems = () => {
  const flat = [];
  menuItems.forEach((item) => {
    flat.push(item);
  });
  return flat;
};

function Navbar() {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check if a path is active
  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Mobile drawer content
  const drawer = (
    <Box sx={{ pt: 2, pb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <LogoText variant="h5" sx={{ color: "#0e3158" }}>Sentry Law</LogoText>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <List>
        {getFlatMenuItems().map((item) => (
          <ListItem
            button
            component={RouterLink}
            to={item.path || "#"}
            key={item.name}
            sx={{
              py: 1.5,
              pl: 2,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
              backgroundColor: isActive(item.path)
                ? "rgba(0,0,0,0.05)"
                : "transparent",
              borderLeft: isActive(item.path) ? "3px solid #f0b94d" : "none",
            }}
            onClick={handleDrawerToggle}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: isActive(item.path) ? "#f0b94d" : "#0e3158",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                fontSize: "1rem",
                fontWeight: isActive(item.path) ? 700 : 600,
                color: isActive(item.path) ? "#0e3158" : "inherit",
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Main Navigation Bar */}
      <NavBar elevation={0}>
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 1, justifyContent: "space-between" }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RouterLink to="/" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <img 
                    src={`${window.wpData?.themeUrl || ''}/public/logo.png`}
                    alt="Logo" 
                    style={{ height: "72px", marginRight: "12px", padding: "4px" }} 
                  />
                </Box>
              </RouterLink>
            </Box>

            {/* Desktop Navigation Menu */}
            {!isMobile && (
              <Box sx={{ display: "flex", justifyContent: "center", flexGrow: 1 }}>
                {menuItems.map((item) => (
                  <NavButton
                    key={item.name}
                    component={RouterLink}
                    to={item.path}
                    $active={isActive(item.path)}
                  >
                    {item.name}
                  </NavButton>
                ))}
              </Box>
            )}

            {/* Phone Number */}
            {!isMobile && (
              <PhoneNumber variant="body2">
              <PhoneIcon fontSize="small" />
              877-529-9498
              </PhoneNumber>
            )}
          </Toolbar>
        </Container>
      </NavBar>


      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;
